import { useEffect, useState } from "react";
import { Button, Form, Input, Row, Col, DatePicker, Checkbox } from "antd";
import FormLayout, {
    FormContent,
    FormFooter,
    FormHeader,
} from "../../../../components/form/form";
import { TKPICycle } from "../../../../types/config";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import useToast from "../../../../hooks/useToast";
import { ToastOpen, ToastType } from "../../../../state/toast/slice";
import {
    GetSpecificKPICycle,
    addEditCycle,
} from "../../../../utils/api.constant";
import { requiredFieldValidator } from "../../../../utils/input.validator";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

type TDrawerProps = {
    id: number;
    onCloseDrawer: () => void;
    onRefreshList: () => void;
};

export default function KPICycleForm(props: TDrawerProps) {
    const [form] = Form.useForm();
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { post } = useFetch();
    const { onShowToast } = useToast();

    useEffect(() => {
        getSpecificKPICycle();
    }, []);

    const defaultValues: Partial<TKPICycle> = {
        id: 0,
        name: "",
        description: "",
        isActive: 1,
    };

    const onFinish = async (values: any) => {
        try {
            const payload = {
                data: {
                    ...values,
                    id: props.id ? props.id : 0,
                    startDate: values.startDate ? values.startDate.format("YYYY-MM-DD") : null,
                    endDate: values.endDate ? values.endDate.format("YYYY-MM-DD") : null,
                    isActive: values.isActive ? 1 : 0,
                },
            };
            setIsLoading(true);

            const response = await post(addEditCycle, payload);
            if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
                onShowToast({
                    type: ToastType.success,
                    title: <CheckOutlined />,
                    position: ToastOpen.centerTop,
                    content: response.dataResponse.description,
                });
                form.resetFields();
                props.onRefreshList();
                props.onCloseDrawer();
            } else {
                onShowToast({
                    type: ToastType.error,
                    title: <CloseOutlined />,
                    position: ToastOpen.centerTop,
                    content: response.dataResponse.description,
                });
            }
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getSpecificKPICycle = async () => {
        if (props.id) {
            try {
                const payload = {
                    data: {
                        id: props.id,
                    },
                };
                setIsLoading(true);
                const response = await post(GetSpecificKPICycle, payload);
                if (response.dataResponse.returnCode === eResultCode.SUCCESS) {
                    const { data } = response;
                    form.setFieldsValue({
                        ...data,
                        startDate: data.startDate ? dayjs(data.startDate) : null,
                        endDate: data.endDate ? dayjs(data.endDate) : null,
                        isActive: data.isActive === 1,
                    });
                } else {
                    onShowToast({
                        type: ToastType.error,
                        title: <CloseOutlined />,
                        position: ToastOpen.centerTop,
                        content: response.dataResponse.description,
                    });
                }
            } catch (error) {
                console.log(error);
            } finally {
                setIsLoading(false);
            }
        }
    };

    return (
        <FormLayout>
            <Form
                form={form}
                name="kpi_cycle_form"
                onFinish={onFinish}
                initialValues={defaultValues}
                layout="vertical"
            >
                <FormHeader>
                    <CloseOutlined
                        style={{ cursor: "pointer", marginTop: "0.3rem", color: "white" }}
                        onClick={props.onCloseDrawer}
                    />
                </FormHeader>

                <FormContent>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="name"
                                label="Cycle Name"
                                rules={[
                                    {
                                        required: true,
                                        validator: requiredFieldValidator("enter cycle name"),
                                    },
                                ]}
                            >
                                <Input placeholder="Enter cycle name" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="startDate"
                                label="Start Date"
                                rules={[{ required: true, message: "Select start date" }]}
                            >
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="endDate"
                                label="End Date"
                                rules={[{ required: true, message: "Select end date" }]}
                            >
                                <DatePicker style={{ width: "100%" }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                name="description"
                                label="Description"
                                rules={[
                                    {
                                        required: true,
                                        validator: requiredFieldValidator("enter description"),
                                    },
                                ]}
                            >
                                <Input.TextArea rows={4} placeholder="Enter description" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="isActive" valuePropName="checked">
                                <Checkbox>Is Active</Checkbox>
                            </Form.Item>
                        </Col>
                    </Row>
                </FormContent>

                <FormFooter className="formFooter">
                    <Button
                        type="default"
                        disabled={isLoading}
                        className="btnDimensions"
                        onClick={props.onCloseDrawer}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        className="btnDimensions"
                        onClick={form.submit}
                        loading={isLoading}
                    >
                        Save
                    </Button>
                </FormFooter>
            </Form>
        </FormLayout>
    );
}
